import { Box } from "@mui/material";
import { useRef, useEffect, useState } from "react";


function animateFoobarStyle(analyser, canvas, canvasCtx, dataArray, bufferLength, peakPositions, peakVelocities, themeMode = 'dark') {
  analyser.getByteFrequencyData(dataArray);
  
  // Clear the canvas with a semi-transparent black to create trail effect
  canvasCtx.fillStyle = "rgba(0, 0, 0, 0.3)";
  canvasCtx.fillRect(0, 0, canvas.width, canvas.height);
  
  // Constants for visualization
  const TARGET_BARS = 30;
  const barSpacing = 3;
  const barWidth = canvas.width / TARGET_BARS - barSpacing; // exact — fills the full canvas width
  const baseHeight = canvas.height;
  const multiplier = 0.85;     // more headroom — bars never hit ceiling even at max volume
  const maxBarHeight = baseHeight * 0.82;

  // Logarithmic frequency mapping — mirrors human hearing.
  // Maps bar i to a bin index that increases exponentially across the audio spectrum,
  // so bass/mids (where music energy lives) get proper bar coverage instead of empty highs.
  const minBin = 1;                                   // ~20 Hz (skip DC bin 0)
  const maxBin = Math.floor(bufferLength * 0.45);     // ~10 kHz — top of meaningful electronic music content

  let x = 0;
  const positions = [];

  for (let i = 0; i < TARGET_BARS; i++) {
    // Compute the log-scaled start and end bin for this bar's frequency band
    const fracStart = i / TARGET_BARS;
    const fracEnd   = (i + 1) / TARGET_BARS;
    const binStart  = Math.round(minBin * Math.pow(maxBin / minBin, fracStart));
    const binEnd    = Math.round(minBin * Math.pow(maxBin / minBin, fracEnd));

    // Average the energy across the band so sparse high-frequency bands still show signal
    let sum = 0, count = 0;
    for (let b = binStart; b <= Math.min(binEnd, bufferLength - 1); b++) {
      sum += dataArray[b];
      count++;
    }
    const raw = count > 0 ? (sum / count) / 255.0 : 0;
    const easedValue  = Math.pow(raw, 0.8);  // gentle curve — more space between quiet and loud bars
    const barHeight   = Math.min(maxBarHeight, easedValue * baseHeight * multiplier);

    positions.push({ x, height: barHeight, value: easedValue, index: binStart });
    x += barWidth + barSpacing;
  }
  
  // Horizontal gradient across all bars: deep red (bass) → amber (treble)
  // This makes each frequency range visually distinct at a glance.
  const hGradient = canvasCtx.createLinearGradient(0, 0, canvas.width, 0);
  hGradient.addColorStop(0.00, '#AA0000');  // bass
  hGradient.addColorStop(0.30, '#FF2200');  // low-mid
  hGradient.addColorStop(0.60, '#FF5500');  // mid
  hGradient.addColorStop(0.85, '#FF8800');  // high-mid
  hGradient.addColorStop(1.00, '#FFAA22');  // treble

  // Draw bars with per-bar vertical fade (top = hGradient color, bottom = dark)
  positions.forEach((pos, idx) => {
    if (pos.height < 1) return;
    // Vertical gradient per bar: top bright (from hGradient slice), bottom near-black
    const barGrad = canvasCtx.createLinearGradient(0, baseHeight - pos.height, 0, baseHeight);
    const fraction = positions.length > 1 ? idx / (positions.length - 1) : 0;
    // Interpolate hue: bass red → treble amber
    const r = Math.round(170 + 85 * fraction);
    const g = Math.round(0   + 170 * fraction);
    const bC = 0;
    barGrad.addColorStop(0,   `rgba(${r},${g},${bC},1)`);
    barGrad.addColorStop(0.6, `rgba(${Math.round(r*0.55)},${Math.round(g*0.4)},0,1)`);
    barGrad.addColorStop(1,   `rgba(${Math.round(r*0.15)},0,0,1)`);
    canvasCtx.fillStyle = barGrad;
    canvasCtx.fillRect(pos.x, baseHeight - pos.height, barWidth, pos.height);

    // Update peak — gravity-based fall
    if (pos.height >= (peakPositions[idx] ?? 0)) {
      peakPositions[idx]  = pos.height;
      peakVelocities[idx] = 0;           // reset velocity when peak is hit
    } else {
      peakVelocities[idx] = (peakVelocities[idx] ?? 0) + 0.09; // gravity
      peakPositions[idx]  = Math.max(0, peakPositions[idx] - peakVelocities[idx]);
    }

    // Subtle reflection below baseline
    const reflGrad = canvasCtx.createLinearGradient(0, baseHeight, 0, baseHeight + 14);
    reflGrad.addColorStop(0, `rgba(${r},${g},0,0.22)`);
    reflGrad.addColorStop(1, `rgba(${r},0,0,0)`);
    canvasCtx.fillStyle = reflGrad;
    canvasCtx.fillRect(pos.x, baseHeight, barWidth, Math.min(14, 18 * pos.value));
  });

  // Peak indicators — glowing white dots that fall under gravity
  positions.forEach((pos, idx) => {
    const pk = peakPositions[idx] ?? 0;
    if (pk < 2) return;
    canvasCtx.save();
    canvasCtx.shadowColor = 'rgba(255,255,255,0.9)';
    canvasCtx.shadowBlur  = 6;
    canvasCtx.fillStyle   = '#FFFFFF';
    canvasCtx.fillRect(pos.x, baseHeight - pk - 2, barWidth, 3);
    canvasCtx.restore();
  });
  
  return positions.length; // Return the actual number of positions for peak tracking
}

// ─── Hacker-style canvas text states ──────────────────────────────────────
const GLYPHS = '!@#$%<>[]01ABCDEFabcdef?/\\|=+~^&*';

function hackerDecode(label, t, cycleSec = 4) {
  const tLocal = t % cycleSec;
  const revealDur = Math.min(1.4, cycleSec * 0.45);
  const revealed = tLocal >= revealDur
    ? label.length
    : Math.floor((tLocal / revealDur) * label.length);

  if (revealed >= label.length) {
    const cursor = Math.floor(t * 1.8) % 2 === 0 ? '_' : ' ';
    return label + cursor;
  }
  let out = '';
  for (let i = 0; i < label.length; i++) {
    out += i < revealed
      ? label[i]
      : GLYPHS[Math.floor((t * 18 + i * 7.3) % GLYPHS.length)];
  }
  return out;
}

function drawHackerState(canvas, ctx, t, label, sublabel, themeMode = 'dark') {
  const w = canvas.width, h = canvas.height;
  ctx.clearRect(0, 0, w, h);

  // Faint ambient falling glyphs - theme aware
  ctx.font = `9px "Courier New", monospace`;
  const cols = Math.ceil(w / 11);
  for (let c = 0; c < cols; c++) {
    const idx = Math.floor((t * 6 + c * 17.3) % GLYPHS.length);
    const y   = ((t * 35 + c * 31) % h);
    ctx.fillStyle = themeMode === 'dark' ? 'rgba(255,34,0,0.05)' : 'rgba(255,34,0,0.08)';
    ctx.fillText(GLYPHS[idx], c * 11, y);
  }

  // Sweeping scanline - theme aware
  const scanY = (t * 55) % h;
  const sg = ctx.createLinearGradient(0, scanY - 8, 0, scanY + 8);
  if (themeMode === 'dark') {
    sg.addColorStop(0,   'rgba(255,34,0,0)');
    sg.addColorStop(0.5, 'rgba(255,34,0,0.07)');
    sg.addColorStop(1,   'rgba(255,34,0,0)');
  } else {
    sg.addColorStop(0,   'rgba(255,34,0,0)');
    sg.addColorStop(0.5, 'rgba(255,34,0,0.1)');
    sg.addColorStop(1,   'rgba(255,34,0,0)');
  }
  ctx.fillStyle = sg;
  ctx.fillRect(0, scanY - 8, w, 16);

  // Main decoded text - theme aware
  const fontSize = Math.max(16, Math.floor(h * 0.24));
  ctx.font        = `bold ${fontSize}px "Courier New", monospace`;
  ctx.textAlign   = 'center';
  ctx.shadowColor = themeMode === 'dark' ? '#FF2200' : '#CC3300';
  ctx.shadowBlur  = themeMode === 'dark' ? 14 : 10;
  ctx.fillStyle   = themeMode === 'dark' ? '#FF4400' : '#DD3300';
  ctx.fillText(hackerDecode(label, t), w / 2, h * 0.52);
  ctx.shadowBlur  = 0;

  // Small status line at bottom - theme aware
  if (sublabel) {
    const sf = Math.max(8, Math.floor(h * 0.085));
    ctx.font      = `${sf}px "Courier New", monospace`;
    ctx.fillStyle = themeMode === 'dark' ? 'rgba(255,68,0,0.45)' : 'rgba(204,51,0,0.6)';
    ctx.fillText(sublabel, w / 2, h - 4);
  }
}

function drawIdle(canvas, ctx, t, themeMode) {
  drawHackerState(canvas, ctx, t, 'PRESS PLAY', '> READY', themeMode);
}

function drawLoading(canvas, ctx, t, themeMode) {
  drawHackerState(canvas, ctx, t, 'LOADING...', '> FETCHING AUDIO STREAM', themeMode);
}

function drawBuffering(canvas, ctx, t, themeMode) {
  drawHackerState(canvas, ctx, t, 'BUFFERING...', '> FILLING BUFFER', themeMode);
}

function drawStopped(canvas, ctx, t, themeMode) {
  drawHackerState(canvas, ctx, t, 'PAUSED', '> PRESS PLAY TO RESUME', themeMode);
}

function drawUnderConstruction(canvas, ctx, t, primary, secondary, themeMode) {
  drawHackerState(canvas, ctx, t, primary || 'UNDER CONSTRUCTION', secondary || '> SOON', themeMode);
}

// ─── Component ─────────────────────────────────────────────────────────────
const Visualizer = ({ data, buffering, loading, isPlaying, placeholderMode = false, placeholderPrimary = 'UNDER CONSTRUCTION', placeholderSecondary = '> SOON', themeMode = 'dark' }) => {
  const canvasRef = useRef(null);
  const peakPositionsRef = useRef([]);
  const peakVelocitiesRef = useRef([]);
  const animationRef = useRef(null);
  const { dataArray, analyzer, bufferLength } = data || {};
  const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const handleResize = () => {
      const canvas = canvasRef.current;
      if (canvas) {
        const containerWidth = canvas.parentElement.clientWidth;
        setCanvasSize({ width: containerWidth, height: 120 });
      }
    };
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !canvasSize.width) return;
    const ctx = canvas.getContext('2d');

    if (animationRef.current) cancelAnimationFrame(animationRef.current);

    const animate = () => {
      animationRef.current = requestAnimationFrame(animate);

      if (placeholderMode) {
        const t = performance.now() / 1000;
        drawUnderConstruction(canvas, ctx, t, placeholderPrimary, placeholderSecondary, themeMode);
      } else if (analyzer && isPlaying && !buffering && !loading) {
        // Frequency bars — resets canvas dimensions each frame (existing behaviour)
        canvas.width  = canvasSize.width;
        canvas.height = canvasSize.height;
        const count = animateFoobarStyle(
          analyzer, canvas, ctx, dataArray, bufferLength, peakPositionsRef.current, peakVelocitiesRef.current, themeMode
        );
        if (peakPositionsRef.current.length !== count) {
          peakPositionsRef.current  = new Array(count).fill(0);
          peakVelocitiesRef.current = new Array(count).fill(0);
        }
      } else {
        const t = performance.now() / 1000;
        if (loading)         drawLoading(canvas, ctx, t, themeMode);
        else if (buffering)  drawBuffering(canvas, ctx, t, themeMode);
        else if (analyzer)   drawStopped(canvas, ctx, t, themeMode);
        else                 drawIdle(canvas, ctx, t, themeMode);
      }
    };

    animate();
    return () => { if (animationRef.current) cancelAnimationFrame(animationRef.current); };
  }, [dataArray, analyzer, bufferLength, canvasSize, buffering, loading, isPlaying, placeholderMode, placeholderPrimary, placeholderSecondary, themeMode]);

  return (
    <Box sx={{
      width: '100%',
      display: 'flex',
      justifyContent: 'center',
      borderRadius: '8px',
      overflow: 'hidden',
      boxShadow: '0 4px 8px rgba(0,0,0,0.3)',
    }}>
      <canvas
        ref={canvasRef}
        width={canvasSize.width}
        height={canvasSize.height}
        style={{
          backgroundColor: 'black',
          width: '100%',
          height: `${canvasSize.height}px`,
          borderRadius: '8px',
          display: 'block',
        }}
      />
    </Box>
  );
};

export default Visualizer;